import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ChevronDown, ExternalLink, CheckCircle2 } from "lucide-react";
import { NEW_AUTHORITY_SCAMS, REAL_BROKER_EMAIL } from "../../data/newAuthorityScams";

function ScamRow({ s, open, onToggle }) {
  return (
    <div className="border-t border-white/[0.06]" data-testid={`scam-${s.id}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3 py-3 text-left" data-testid={`scam-toggle-${s.id}`}>
        <span className="text-sm font-semibold text-white">{s.title}</span>
        <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 space-y-3 text-[13px]" data-testid={`scam-body-${s.id}`}>
          <p className="text-white/60 italic border-l-2 border-red-400/40 pl-3">{s.hook}</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1">Tells</div>
            <ul className="space-y-1 text-white/75">{s.tells.map((t) => <li key={t}>· {t}</li>)}</ul>
          </div>
          {s.onCall !== "—" && <p className="text-white/60"><span className="text-white/40">If you engage:</span> {s.onCall}</p>}
          <p className="text-emerald-200"><span className="text-emerald-400 font-bold">Your move:</span> {s.move}</p>
        </div>
      )}
    </div>
  );
}

/* Public checklist card — the scams that hit a new MC in its first 30 days, and what a real broker looks like. */
export function ScamAlertCard() {
  const [open, setOpen] = useState(NEW_AUTHORITY_SCAMS[0].id);
  const [showReal, setShowReal] = useState(false);
  return (
    <section className="mt-10 rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-5 sm:p-6" data-testid="scam-alert-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-red-300 font-bold"><ShieldAlert className="w-3.5 h-3.5" /> New-authority scam alert</div>
          <h3 className="text-white font-bold text-base sm:text-lg mt-1">Your MC gets scraped the day it posts. Here's who calls first.</h3>
          <p className="text-white/55 text-sm mt-1 max-w-2xl">Expect 20–50 of these by email and text in your first month. None of them found you "on a platform." Rule: if there is no MC or DOT to check, there is no conversation.</p>
        </div>
        <Link to="/broker-check" className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A1F] hover:bg-[#ff6e3a] text-black text-xs font-bold px-4 py-2 transition-colors" data-testid="scam-brokercheck-link">
          Check a broker <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
      <div className="mt-3" data-testid="scam-list">
        {NEW_AUTHORITY_SCAMS.map((s) => <ScamRow key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} />)}
      </div>
      <div className="mt-4 border-t border-white/[0.06] pt-4">
        <button type="button" onClick={() => setShowReal((v) => !v)} className="text-sm font-semibold text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1.5" data-testid="scam-real-broker-toggle">
          <CheckCircle2 className="w-4 h-4" /> What a real broker email looks like <ChevronDown className={`w-4 h-4 transition-transform ${showReal ? "rotate-180" : ""}`} />
        </button>
        {showReal && (
          <ul className="mt-2 space-y-1 text-[13px] text-white/75" data-testid="scam-real-broker-list">
            {REAL_BROKER_EMAIL.map((t) => <li key={t}>· {t}</li>)}
          </ul>
        )}
        <div className="mt-3 text-[11px] text-white/40">
          Got hit? Report to FMCSA's <a href="https://nccdb.fmcsa.dot.gov/nccdb/home.aspx" target="_blank" rel="noreferrer" className="underline hover:text-white/70" data-testid="scam-report-nccdb">National Consumer Complaint Database</a> and the <a href="https://reportfraud.ftc.gov" target="_blank" rel="noreferrer" className="underline hover:text-white/70" data-testid="scam-report-ftc">FTC</a>.
        </div>
      </div>
    </section>
  );
}
